import React, { useEffect, useCallback, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Navigate, useLocation } from "react-router-dom";
import { Spin } from "antd";
import {
  initializeAuth,
  fetchUserDataAction,
} from "../slices/authSlice";
import { useIdleTimer, isSessionStale, clearActivityTimestamp } from "../hooks/useIdleTimer";
import { performLogout } from "../utils/performLogout";
import { selectPersonaGateDecision } from "../hooks/usePersonaConfig";
import { selectActiveWorkspaceConfigSettled } from "../slices/workspaceConfigSlice";
import ProfileLoadFailed from "./ProfileLoadFailed";

// Two gates hold the whole viewport while something the app cannot render
// without is still resolving: Gate 3 on the user profile, Gate 5 on the
// persona read. One definition so a restyle cannot reach only one of them.
const fullScreenSpinner = (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
    <Spin size="large" />
  </div>
);

const ProtectedRoute = ({ children }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const {
    isInitialized,
    isAuthenticated,
    user,
    userData,
    userDataFetchedThisSession,
    userDataFetchError,
  } = useSelector((state) => state.auth);
  const isImpersonating = useSelector((state) => state.auth?.isImpersonating);
  const personaGate = useSelector(selectPersonaGateDecision);
  // Gate 3 also holds the first render for each acting identity until its
  // landing workspace's config settles, so no page paints the default layout
  // and then swaps it. Keying the latch on the uid re-arms it for an
  // impersonation enter or exit. Within one identity only the first load
  // waits here: a later switch waits on `auth.status` instead, which the
  // switch thunk holds until the new workspace's config arrives.
  const workspaceConfigSettled = useSelector(selectActiveWorkspaceConfigSettled);
  const configSettledForUidRef = useRef(null);
  if (workspaceConfigSettled && userDataFetchedThisSession) {
    configSettledForUidRef.current = user?.uid ?? null;
  }
  const bootConfigSettled =
    configSettledForUidRef.current !== null &&
    configSettledForUidRef.current === (user?.uid ?? null);
  // Session-age check: runs synchronously on first render BEFORE any effects.
  // Must execute before useIdleTimer's effect writes a fresh timestamp to localStorage.
  const sessionStaleRef = useRef(null);
  if (sessionStaleRef.current === null) {
    sessionStaleRef.current = isAuthenticated && isSessionStale();
    if (sessionStaleRef.current) {
      clearActivityTimestamp();
    }
  }

  // Stale session on load → hard-logout to /login with the inactivity banner
  // (matches the idle path below), rather than a silent SPA redirect.
  useEffect(() => {
    if (sessionStaleRef.current) {
      performLogout(dispatch, "session_expired");
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Idle timer: log out after 1 hour of inactivity (covers both DashONE and DashAI).
  // Hard-navigates to /login (performLogout) so the idle-logout→re-login cycle
  // re-fetches index.html and lands on the latest bundle.
  const handleIdle = useCallback(() => {
    clearActivityTimestamp();
    performLogout(dispatch, "session_expired");
  }, [dispatch]);

  useIdleTimer(handleIdle, 3600000);

  // A retry from the failure screen clears the error the moment it starts
  // (the pending reducer), and the previous profile is still in state, so
  // without this flag Gate 3 would let the stale app render for the whole
  // request and snap back to the screen on failure. The screen stays up until
  // the retry settles.
  const [retryingProfile, setRetryingProfile] = useState(false);
  const retryProfileFetch = useCallback(() => {
    setRetryingProfile(true);
    dispatch(fetchUserDataAction({ withScope: true })).finally(() =>
      setRetryingProfile(false)
    );
  }, [dispatch]);

  useEffect(() => {
    if (!isInitialized) {
      dispatch(initializeAuth());
    }
  }, [dispatch, isInitialized]);

  useEffect(() => {
    if (!isAuthenticated || !user) return;
    // Skip re-fetching if user data was already fetched this session.
    // This prevents workspace selection from resetting on navigation between
    // DashAI and DashONE. The flag resets on page reload and logout.
    // Also require userData to be present: a corrupted persisted auth state can
    // rehydrate this flag as true while userData is still null, and that must
    // trigger a refetch rather than leave the session stranded with no profile.
    if (userDataFetchedThisSession && userData) return;
    // Skip if session is stale — logout is in progress
    if (sessionStaleRef.current) return;
    let cancelled = false;

    (async () => {
      try {
        await dispatch(fetchUserDataAction({ withScope: true })).unwrap();
      } catch (err) {
        if (!cancelled) {
          console.error("ProtectedRoute: fetch user failed", err);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [dispatch, isAuthenticated, user, userDataFetchedThisSession, userData]);

  // Gate 1: Session stale or not authenticated -> redirect to login
  // Preserve the intended destination so LoginPage can return the user there
  // after a successful sign-in (e.g. Slack-shared /chat/:chatId/:canvasId links).
  if (sessionStaleRef.current || !isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  // Gate 2: the profile fetch failed while Firebase is still signed in. Hold
  // the session and show the user a way out: retry, or sign out through the
  // shared teardown. Sending them to /login here would present a form that
  // cannot change the state that sent them there.
  if (userDataFetchError || retryingProfile) {
    if (userDataFetchError) {
      console.error("ProtectedRoute: User data fetch failed:", userDataFetchError);
    }
    return (
      <ProfileLoadFailed
        error={userDataFetchError}
        retrying={retryingProfile}
        onRetry={retryProfileFetch}
      />
    );
  }

  // Gate 3: Fresh user data not yet loaded -> show spinner
  // Blocks all downstream components from using stale workspace/id_client from localStorage.
  // The `|| !userData` guard makes this self-healing: if a corrupted persisted
  // state rehydrates the fetch flag as true while userData is null, show the
  // spinner (the effect above then refetches) instead of rendering a
  // profile-less app with empty dashboards and a generic footer.
  if (!userDataFetchedThisSession || !userData || !bootConfigSettled) {
    return fullScreenSpinner;
  }

  // Gate 4: An admin reset this user's password -> force a password change
  // before any app content renders. Placed after Gate 3 so userData is the
  // freshly-fetched profile. The pathname guard prevents a redirect loop while
  // the force-change screen itself renders inside ProtectedRoute.
  if (
    userData?.force_password_change &&
    location.pathname !== "/force-password-change"
  ) {
    return <Navigate to="/force-password-change" replace />;
  }

  // Gate 5: no persona config -> onboarding wizard. The ABSENCE of a config
  // is the trigger, never a hasOnboarded flag, so clearing the config is the
  // QA reset and production gets once-per-user for free.
  //
  // The config lives on the server, so absence is only knowable once that
  // read has settled. `selectPersonaGateDecision` returns that as
  // one answer rather than a boolean: "wait" while the fetch is in flight,
  // and "allow" when it FAILED, because a read that could not report is not
  // a report of absence — redirecting on it would walk an onboarded user
  // through the wizard and overwrite the config they already had.
  //
  // Skipped while impersonating, where no fetch runs after the identity swap
  // so the target's config is never loaded: the impersonated view falls back
  // to the unfiltered everything-view rather than trapping the admin in a
  // wizard whose answers the target never chose. The pathname guards prevent
  // redirect loops for the two standalone routes.
  const onOnboardingRoute =
    location.pathname === "/onboarding" ||
    location.pathname === "/force-password-change";

  if (personaGate === "wait" && !isImpersonating && !onOnboardingRoute) {
    return fullScreenSpinner;
  }

  if (personaGate === "onboard" && !isImpersonating && !onOnboardingRoute) {
    // Carry the requested destination so finishing the wizard returns the
    // user there (a config-less user opening a shared deep link must not
    // lose it) — same pattern as the login gate above.
    return <Navigate to="/onboarding" replace state={{ from: location }} />;
  }

  return children;
};

export default ProtectedRoute;
